import { useId, useState, type FormEvent } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { LiksIconPicker } from '../../components/liks/LiksIconPicker/LiksIconPicker';
import './CreateKnowledgeDatabaseDialog.css';

const CREATE_KNOWLEDGE_DEFAULT_ICON = 'book';

export type CreateKnowledgeDatabaseDialogProps = {
  titleLabel: string;
  descriptionLabel: string;
  iconLabel: string;
  iconSearchPlaceholder: string;
  iconSearchClearLabel: string;
  iconNoResultsLabel: string;
  getIconOptionLabel: (iconName: string) => string;
  titlePlaceholder: string;
  descriptionPlaceholder: string;
  createLabel: string;
  cancelLabel: string;
  closeLabel: string;
  onCreate: (input: {
    name: string;
    description: string;
    icon: string;
  }) => void;
  onClose: () => void;
};

export function CreateKnowledgeDatabaseDialog({
  titleLabel,
  descriptionLabel,
  iconLabel,
  iconSearchPlaceholder,
  iconSearchClearLabel,
  iconNoResultsLabel,
  getIconOptionLabel,
  titlePlaceholder,
  descriptionPlaceholder,
  createLabel,
  cancelLabel,
  closeLabel,
  onCreate,
  onClose,
}: CreateKnowledgeDatabaseDialogProps) {
  const titleFieldId = useId();
  const descriptionId = useId();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState(CREATE_KNOWLEDGE_DEFAULT_ICON);
  const isCreateDisabled = name.trim() === '' || icon.trim() === '';

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onCreate({ name, description, icon });
  };

  return (
    <LiksDialog
      isOpen
      as="form"
      size="md"
      title={createLabel}
      onClose={onClose}
      onSubmit={handleSubmit}
      closeLabel={closeLabel}
      footer={
        <>
          <LiksButton variant="secondary" type="button" onClick={onClose}>
            {cancelLabel}
          </LiksButton>
          <LiksButton
            variant="primary"
            type="submit"
            disabled={isCreateDisabled}
          >
            {createLabel}
          </LiksButton>
        </>
      }
    >
      <label className="liks-knowledge-create__field" htmlFor={titleFieldId}>
        <span className="liks-knowledge-create__label">{titleLabel}</span>
        <input
          id={titleFieldId}
          className="liks-knowledge-create__input"
          value={name}
          placeholder={titlePlaceholder}
          onChange={(event) => setName(event.target.value)}
          required
          autoFocus
        />
      </label>

      <label className="liks-knowledge-create__field" htmlFor={descriptionId}>
        <span className="liks-knowledge-create__label">{descriptionLabel}</span>
        <textarea
          id={descriptionId}
          className="liks-knowledge-create__textarea"
          value={description}
          placeholder={descriptionPlaceholder}
          onChange={(event) => setDescription(event.target.value)}
          rows={4}
        />
      </label>

      <LiksIconPicker
        value={icon}
        onChange={setIcon}
        label={iconLabel}
        searchPlaceholder={iconSearchPlaceholder}
        searchClearLabel={iconSearchClearLabel}
        noResultsLabel={iconNoResultsLabel}
        getOptionLabel={getIconOptionLabel}
      />
    </LiksDialog>
  );
}
